import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../core/auth/AuthContext";
import { captureAuthSession } from "../../core/auth/authSession";
import { useModalAccessibility } from "../../hooks/useModalAccessibility";
import { getGeolocalisation } from "../../map/mapUtils/getGeolocalisation";
import { forwardGeocodeAutocomplete } from "../../utils/forwardGeocode";
import { reverseGeocodeCoordinates } from "../../utils/reverseGeocode";
import { updateUserProfile } from "../profile/services/profile.repository";
import { CitySearchPanel, CityTrigger, DEFAULT_CITY, PinIcon, cityName } from "./CityPicker";
import {
  LOCATION_PROMPT_FIRST_DELAY_MS,
  LOCATION_PROMPT_RETRY_MS,
  canUseDeviceGeolocation,
  isAnotherModalOpen,
  isLocationPromptEligible,
  isLocationPromptSnoozed,
  snoozeLocationPrompt,
} from "./locationPrompt.model";
// Meme langage visuel que la fenetre des notifications push.
import shell from "../../components/notifications/PushNotificationBanner.module.css";
import styles from "./LocationPrompt.module.css";

const DONE_CLOSE_DELAY_MS = 1600;
// Une seule ouverture par session et par membre, meme si la mise en page change.
const shownInSession = new Set();

// Ville proposee par defaut : partagee avec « Ma ville » du profil.
export { DEFAULT_CITY };

async function readGeolocationPermission() {
  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state;
  } catch {
    return null;
  }
}

// Ville actuelle du membre (Lyon par defaut ou estimee), proposee a confirmer.
function cityFromUser(user) {
  const location = user?.location;
  const [lng, lat] = Array.isArray(location?.coordinates) ? location.coordinates : [];
  if (!location?.city || !Number.isFinite(lat) || !Number.isFinite(lng)) return DEFAULT_CITY;
  const [, ...rest] = String(location.label || "").split(",");
  return { city: location.city, label: location.label || location.city, sublabel: rest.join(",").trim(), coordinates: [lng, lat] };
}

const DEFAULT_SERVICES = Object.freeze({
  locate: () => getGeolocalisation({ enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }),
  // Seule la ville est gardee : jamais de position GPS precise enregistree.
  findCity: async ([lng, lat]) => (await reverseGeocodeCoordinates([lng, lat], { fallbackCityLabel: "" }))?.city || "",
  // Meme enregistrement que « Ma ville » dans l'edition du profil, lie a la
  // session du membre (jamais envoye ni rejoue pour un autre compte).
  saveCity: async (userId, { city, lat, lng, label }) => {
    const session = captureAuthSession();
    if (session.uid !== userId) {
      throw Object.assign(new Error("La session a change."), { code: "auth/session-changed" });
    }
    return updateUserProfile(userId, {
      location: { city, lat, lng, label },
      isCustomLocation: true,
    }, { firebaseUser: session.user, sessionGuard: session.isCurrent });
  },
  searchCities: (query) => forwardGeocodeAutocomplete(query, { limit: 5 }),
  readPermission: () => (canUseDeviceGeolocation() ? readGeolocationPermission() : Promise.resolve("denied")),
});

export default function LocationPrompt() {
  const { user } = useAuth();
  // Une instance par membre : un changement de compte demonte la fenetre et
  // repart d'un etat vierge (choix, messages, recherche, operations en cours).
  return <LocationPromptHost key={user?.uid || "signed-out"} user={user} />;
}

// services : injectables pour l'apercu local (?preview=location-prompt).
export function LocationPromptHost({ user, services = DEFAULT_SERVICES }) {
  const userId = user?.uid || null;
  const eligible = isLocationPromptEligible(user);
  // phase : confirm | locating | saving | done
  const [phase, setPhase] = useState(null);
  const [message, setMessage] = useState("");
  const [selectedCity, setSelectedCity] = useState(() => cityFromUser(user));
  const [savedCity, setSavedCity] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [gpsAvailable, setGpsAvailable] = useState(false);
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  // Une suite asynchrone (GPS, recherche, sauvegarde) n'agit que si la fenetre
  // est toujours montee pour le membre qui l'a lancee : jamais d'etat, de
  // message ni d'ecriture pour un autre membre.
  const aliveRef = useRef(false);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);
  const isLive = (operationUserId) => aliveRef.current && userIdRef.current === operationUserId;
  const isOpen = Boolean(phase);
  useModalAccessibility(dialogRef, { active: isOpen });

  useEffect(() => {
    if (!eligible || !userId || shownInSession.has(userId) || isLocationPromptSnoozed(userId)) return undefined;
    let timer = null;
    let cancelled = false;
    const tryOpen = async () => {
      if (cancelled) return;
      if (isAnotherModalOpen()) {
        timer = window.setTimeout(tryOpen, LOCATION_PROMPT_RETRY_MS);
        return;
      }
      const permission = await services.readPermission();
      if (cancelled || isAnotherModalOpen()) {
        if (!cancelled) timer = window.setTimeout(tryOpen, LOCATION_PROMPT_RETRY_MS);
        return;
      }
      shownInSession.add(userId);
      setGpsAvailable(permission !== "denied" && canUseDeviceGeolocation());
      setPhase("confirm");
    };
    timer = window.setTimeout(tryOpen, LOCATION_PROMPT_FIRST_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [eligible, services, userId]);

  useEffect(() => {
    if (phase !== "done") return undefined;
    const timer = window.setTimeout(() => setPhase(null), DONE_CLOSE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  const isBusy = phase === "locating" || phase === "saving";

  const closePicker = useCallback(() => {
    setPickerOpen(false);
  }, []);

  // Retour de l'ecran de recherche : le focus revient sur le champ ville, une
  // fois celui-ci de nouveau affiche.
  const wasPickerOpenRef = useRef(false);
  useEffect(() => {
    if (wasPickerOpenRef.current && !pickerOpen) triggerRef.current?.focus({ preventScroll: true });
    wasPickerOpenRef.current = pickerOpen;
  }, [pickerOpen]);

  const close = useCallback(() => {
    if (isBusy) return;
    if (phase !== "done" && userId) snoozeLocationPrompt(userId);
    setPhase(null);
  }, [isBusy, phase, userId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      // Echap ferme d'abord la liste, puis la fenetre.
      if (pickerOpen) closePicker();
      else close();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [close, closePicker, isOpen, pickerOpen]);

  const openPicker = () => {
    setMessage("");
    setPickerOpen(true);
  };

  const handleConfirm = async () => {
    const [lng, lat] = selectedCity.coordinates;
    const city = cityName(selectedCity);
    const operationUserId = userId;
    setMessage("");
    setPhase("saving");
    try {
      await services.saveCity(operationUserId, { city, lat, lng, label: selectedCity.label || city });
      if (!isLive(operationUserId)) return;
      setSavedCity(city);
      setPhase("done");
    } catch {
      if (!isLive(operationUserId)) return;
      setMessage("Enregistrement impossible. Réessaie dans un instant.");
      setPhase("confirm");
    }
  };

  const handleUsePosition = async () => {
    const operationUserId = userId;
    setPickerOpen(false);
    setMessage("");
    setPhase("locating");
    let coords;
    let city = "";
    try {
      coords = await services.locate();
      city = await services.findCity(coords);
    } catch (error) {
      if (!isLive(operationUserId)) return;
      if (error?.code === 1) setGpsAvailable(false);
      setMessage(error?.code === 1
        ? "Position refusée. Choisis plutôt ta ville dans la liste."
        : "Position introuvable pour le moment. Choisis plutôt ta ville.");
      setPhase("confirm");
      return;
    }
    // Position obtenue apres un changement de membre : jamais enregistree.
    if (!isLive(operationUserId)) return;
    if (!city) {
      setMessage("Ville introuvable pour ta position. Choisis-la dans la liste.");
      setPhase("confirm");
      return;
    }
    const [lng, lat] = coords;
    setPhase("saving");
    try {
      // Meme enregistrement qu'un choix dans la liste : ville confirmee.
      await services.saveCity(operationUserId, { city, lat, lng, label: city });
      if (!isLive(operationUserId)) return;
      setSavedCity(city);
      setPhase("done");
    } catch {
      if (!isLive(operationUserId)) return;
      setMessage("Enregistrement impossible. Réessaie dans un instant.");
      setPhase("confirm");
    }
  };

  const handlePickCity = (suggestion) => {
    setSelectedCity(suggestion);
    closePicker();
  };

  if (!isOpen) return null;

  const selectedName = cityName(selectedCity);
  // Ecran de recherche dedie : hauteur fixe, champ en haut, ligne d'etat de
  // hauteur reservee, seule la liste defile. Rien ne bouge pendant la saisie.
  const showSearch = pickerOpen && phase !== "done";

  return (
    <div className={`${shell.overlay} ${styles.overlay}`} role="presentation" onClick={close}>
      <section
        ref={dialogRef}
        className={`${shell.modal} ${styles.modal} ${showSearch ? styles.modalSearch : ""}`}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="location-prompt-title"
        aria-busy={isBusy}
        onClick={(event) => event.stopPropagation()}
      >
        {showSearch ? (
          <CitySearchPanel
            idPrefix="location-prompt"
            titleId="location-prompt-title"
            selectedCity={selectedCity}
            gpsAvailable={gpsAvailable}
            searchCities={services.searchCities}
            onPickCity={handlePickCity}
            onUseGps={handleUsePosition}
            onBack={closePicker}
            scrollContainerRef={dialogRef}
          />
        ) : (
          <>
            <button type="button" className={shell.close} onClick={close} aria-label="Fermer" disabled={isBusy}>×</button>
            <span className={shell.icon} aria-hidden="true">
              <PinIcon className={styles.pin} done={phase === "done"} />
            </span>
            <div className={shell.copy}>
              {phase === "done" ? (
                <h2 id="location-prompt-title">{savedCity ? `C’est noté : ${savedCity} !` : "C’est noté !"}</h2>
              ) : (
                <h2 id="location-prompt-title">
                  Les sorties à <span className={styles.cityAccent}>{selectedName}</span> t’intéressent&nbsp;?
                </h2>
              )}
              {message ? <small role="status">{message}</small> : null}
            </div>

            {phase === "done" ? null : (
              <>
                <div className={styles.picker}>
                  <CityTrigger city={selectedCity} onClick={openPicker} disabled={isBusy} triggerRef={triggerRef} />
                </div>

                <button type="button" className={shell.cta} onClick={handleConfirm} disabled={isBusy} data-autofocus>
                  {phase === "locating"
                    ? "Recherche de ta position…"
                    : phase === "saving"
                      ? "Enregistrement…"
                      : `Oui, les sorties à ${selectedName}`}
                </button>
                <button type="button" className={shell.later} onClick={close} disabled={isBusy}>Plus tard</button>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
